import { Check, Sparkles } from "lucide-react";
import { PRICING, SITE_CONFIG } from "../../data/mock";
import { Button } from "../ui/button";

const Pricing = () => {
  return (
    <section
      id="pricing"
      aria-labelledby="pricing-heading"
      aria-describedby="pricing-description"
      className="py-24 bg-[var(--bg-page)] transition-theme"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[var(--accent-wash)] text-[var(--accent-text)] text-sm font-medium mb-4">
            Pricing
          </span>
          <h2
            id="pricing-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--text-primary)] mb-6 leading-tight"
          >
            Simple, Transparent Pricing
          </h2>
          <p
            id="pricing-description"
            className="text-lg text-[var(--text-body)] opacity-90"
          >
            Only{" "}
            <span className="font-semibold text-[var(--accent-text)]">
              KES {PRICING.perStudentCost}
            </span>{" "}
            per student per year — affordable for schools of all sizes across
            Kenya.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {PRICING.plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative p-8 rounded-2xl border transition-all duration-300 card-hover ${
                plan.popular
                  ? "bg-gradient-to-b from-[var(--accent-wash)] to-[var(--bg-card)] border-[var(--accent-primary)] shadow-xl shadow-[var(--accent-primary)]/10"
                  : "bg-[var(--bg-card)] border-[var(--border-light)] hover:border-[var(--accent-primary)]/30"
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full btn-gradient text-white text-sm font-medium shadow-lg">
                    <Sparkles className="w-4 h-4" />
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-[var(--text-secondary)] font-medium mb-4">
                  Up to {plan.students} students
                </p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-sm font-medium text-[var(--text-secondary)]">
                    KES
                  </span>
                  <span className="text-4xl font-bold text-[var(--text-primary)]">
                    {plan.price.toLocaleString()}
                  </span>
                  <span className="text-[var(--text-secondary)]">/year</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[var(--accent-wash)] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[var(--accent-text)]" />
                    </div>
                    <span className="text-sm text-[var(--text-body)] opacity-90">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href={SITE_CONFIG.signupFormUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button
                  className={`w-full rounded-full py-6 font-semibold transition-all duration-300 ${
                    plan.popular
                      ? "btn-gradient text-white hover:opacity-90 shadow-lg hover:shadow-xl"
                      : "bg-[var(--bg-section)] text-[var(--text-primary)] hover:bg-[var(--accent-wash)] border border-[var(--border-light)]"
                  }`}
                >
                  Get Started
                </Button>
              </a>
            </div>
          ))}
        </div>

        {/* Enterprise Section */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="p-8 sm:p-12 rounded-2xl bg-gradient-to-r from-[var(--accent-wash)] to-transparent border border-[var(--accent-primary)]/20">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <div>
                <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
                  {PRICING.enterprise.name}
                </h3>
                <p className="text-[var(--text-secondary)] mb-4">
                  {PRICING.enterprise.description}
                </p>
                <div className="flex flex-wrap gap-3">
                  {PRICING.enterprise.features
                    .slice(0, 3)
                    .map((feature, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[var(--bg-card)] border border-[var(--border-light)] text-sm text-[var(--text-secondary)]"
                      >
                        <Check className="w-3 h-3 text-[var(--accent-text)]" />
                        {feature}
                      </span>
                    ))}
                </div>
              </div>
              <a href="#contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 border-[var(--accent-primary)] text-[var(--accent-text)] hover:bg-[var(--accent-wash)] whitespace-nowrap"
                >
                  Contact Sales
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
